// Lightweight developer-vibe primitives shared by /codex/api and /codex/analytics.
// Goals: legibility first, lore second. Mono-first typography, JSON syntax highlight,
// language tabs (curl / JS / Python), copy buttons, live "Try it" fetcher.

import { useState, useMemo, useCallback } from 'react';
import { Copy, ClipboardCheck, Play, Loader2 } from 'lucide-react';

export const MONO = "'JetBrains Mono','Fira Code','SF Mono','Courier New',monospace";
export const SERIF = "'EB Garamond','Cinzel',Georgia,serif";

const palette = {
  bg: '#0a0608',
  bgPanel: 'rgba(14,8,6,0.92)',
  bgCode: 'rgba(0,0,0,0.55)',
  border: 'rgba(140,100,40,0.32)',
  borderStrong: 'rgba(180,140,55,0.6)',
  ink: '#e8d9b4',
  inkSoft: '#9a8456',
  inkMute: '#5a4828',
  gold: '#ffd86b',
  rose: '#ff8b6b',
  green: '#88e878',
  cyan: '#7dd0ff',
  violet: '#a892ff',
  red: '#ff5a4d',
};
export const C = palette;

// JSON syntax highlighter — minimal, dependency-free, regex-based.
function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function highlightJson(value) {
  let str;
  try {
    str = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch {
    str = String(value);
  }
  str = escapeHtml(str);
  // Match strings (keys + values), numbers, bools, null
  str = str.replace(
    /("(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'tk-num';
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? 'tk-key' : 'tk-str';
      } else if (/true|false/.test(match)) {
        cls = 'tk-bool';
      } else if (/null/.test(match)) {
        cls = 'tk-null';
      }
      return `<span class="${cls}">${match}</span>`;
    },
  );
  return str;
}

export function JsonBlock({ value, maxHeight = 360, testId }) {
  const html = useMemo(() => highlightJson(value), [value]);
  return (
    <>
      <style>{`
        .codex-json { color: ${palette.ink}; }
        .codex-json .tk-key  { color: ${palette.gold}; }
        .codex-json .tk-str  { color: ${palette.green}; }
        .codex-json .tk-num  { color: ${palette.violet}; }
        .codex-json .tk-bool { color: ${palette.cyan}; }
        .codex-json .tk-null { color: ${palette.rose}; font-style: italic; }
      `}</style>
      <pre
        data-testid={testId}
        className="codex-json text-[12px] leading-relaxed overflow-auto px-4 py-3"
        style={{
          fontFamily: MONO,
          background: palette.bgCode,
          border: `1px solid ${palette.border}`,
          maxHeight,
          tabSize: 2,
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
}

export function CopyBtn({ text, label = 'COPY', testId }) {
  const [copied, setCopied] = useState(false);
  const onClick = useCallback(async () => {
    const flash = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts / iframes that block clipboard API.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch { /* ignore */ }
        document.body.removeChild(ta);
      }
    } catch {
      // Permission denied — still flash so user sees confirmation, plus the legacy fallback above ran.
    }
    flash();
  }, [text]);
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      type="button"
      className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.18em] hover:brightness-125 transition-all active:scale-[0.97]"
      style={{
        fontFamily: MONO,
        color: copied ? palette.green : palette.inkSoft,
        background: 'rgba(0,0,0,0.4)',
        border: `1px solid ${copied ? palette.green : palette.border}`,
      }}
    >
      {copied ? <ClipboardCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? 'COPIED' : label}
    </button>
  );
}

// Build per-language code samples for a given GET endpoint.
export function buildCodeSamples({ baseUrl, path, samplePath }) {
  const url = `${baseUrl}${samplePath || path}`;
  return {
    curl: `curl -s "${url}"`,
    javascript: `const r = await fetch("${url}");
const data = await r.json();
console.log(data);`,
    python: `import requests

r = requests.get("${url}")
data = r.json()
print(data)`,
  };
}

const LANGS = [
  { id: 'curl', label: 'cURL' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'python', label: 'Python' },
];

export function CodeTabs({ samples, idPrefix }) {
  const [active, setActive] = useState('curl');
  const code = samples[active] || '';
  return (
    <div>
      <div
        className="flex items-stretch border-b"
        style={{ borderColor: palette.border }}
        role="tablist"
      >
        {LANGS.map((l) => {
          const isOn = active === l.id;
          return (
            <button
              key={l.id}
              type="button"
              role="tab"
              aria-selected={isOn}
              data-testid={`${idPrefix}-tab-${l.id}`}
              onClick={() => setActive(l.id)}
              className="px-3 py-1.5 text-[11px] tracking-[0.2em] transition-colors"
              style={{
                fontFamily: MONO,
                color: isOn ? palette.gold : palette.inkSoft,
                borderBottom: `2px solid ${isOn ? palette.gold : 'transparent'}`,
                background: isOn ? 'rgba(255,216,107,0.04)' : 'transparent',
                marginBottom: '-1px',
              }}
            >
              {l.label.toUpperCase()}
            </button>
          );
        })}
        <div className="flex-1" />
        <div className="px-2 py-1 flex items-center">
          <CopyBtn text={code} label="COPY" testId={`${idPrefix}-copy`} />
        </div>
      </div>
      <pre
        data-testid={`${idPrefix}-code`}
        className="text-[12px] overflow-x-auto px-4 py-3 leading-relaxed"
        style={{
          fontFamily: MONO,
          background: palette.bgCode,
          border: `1px solid ${palette.border}`,
          borderTop: 'none',
          color: palette.ink,
          whiteSpace: 'pre',
        }}
      >{code}</pre>
    </div>
  );
}

// "Try it" — fires the actual request, shows status + duration + JSON.
export function TryItButton({ url, idPrefix }) {
  const [state, setState] = useState({ loading: false, ok: null, status: null, ms: null, data: null, err: null });
  const run = async () => {
    setState({ loading: true, ok: null, status: null, ms: null, data: null, err: null });
    const start = performance.now();
    try {
      const r = await fetch(url);
      const ms = Math.round(performance.now() - start);
      let body = null;
      try { body = await r.json(); } catch { body = await r.text(); }
      setState({ loading: false, ok: r.ok, status: r.status, ms, data: body, err: null });
    } catch (e) {
      const ms = Math.round(performance.now() - start);
      setState({ loading: false, ok: false, status: null, ms, data: null, err: String(e) });
    }
  };
  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={run}
          disabled={state.loading}
          data-testid={`${idPrefix}-try`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.18em] transition-all active:scale-[0.97] disabled:opacity-60"
          style={{
            fontFamily: MONO,
            color: palette.gold,
            background: 'linear-gradient(180deg, rgba(40,24,8,0.92) 0%, rgba(20,12,4,1) 100%)',
            border: `1px solid ${palette.borderStrong}`,
          }}
        >
          {state.loading
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <Play className="w-3 h-3" style={{ fill: palette.gold }} />}
          {state.loading ? 'CALLING…' : 'TRY IT LIVE'}
        </button>

        {state.status != null && (
          <span
            data-testid={`${idPrefix}-try-status`}
            className="text-[10px] tracking-[0.18em] px-2 py-0.5"
            style={{
              fontFamily: MONO,
              color: state.ok ? palette.green : palette.red,
              border: `1px solid ${state.ok ? palette.green : palette.red}`,
              background: 'rgba(0,0,0,0.5)',
            }}
          >
            {state.status} {state.ok ? 'OK' : 'ERR'} · {state.ms}ms
          </span>
        )}
        {state.err && (
          <span className="text-[10px]" style={{ color: palette.red, fontFamily: MONO }}>{state.err}</span>
        )}
      </div>
      {state.data != null && (
        <JsonBlock value={state.data} testId={`${idPrefix}-try-response`} maxHeight={300} />
      )}
    </div>
  );
}
